/* eslint react/no-did-mount-set-state: 0 */

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import EdapViewTracker from '../edap-view-tracker/EdapViewTracker';

import {pushEdapEvent} from '../../../utils/edap';

import {CHEVRON_DOWN__16} from '@homeaway/svg-defs';
import {SVGIcon} from '@homeaway/react-svg';

const textItem = PropTypes.shape({
    content: PropTypes.string,
    panelID: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    titleTag: PropTypes.string,
});

export class TextAccordion extends Component {
    static propTypes = {
        ACTION_LOCATION: PropTypes.string,
        EVENT_CATEGORY: PropTypes.string,
        EVENT_TYPE: PropTypes.string,

        baseCls: PropTypes.string,
        containerCls: PropTypes.string,
        panelIDPrefix: PropTypes.string,
        headingStyle: PropTypes.oneOf(['heading', 'span', 'dynamic']),
        text: PropTypes.arrayOf(textItem),
        useStructuredData: PropTypes.bool,
    };

    static defaultProps = {
        ACTION_LOCATION: 'text-component',
        EVENT_CATEGORY: 'interaction-click',
        EVENT_TYPE: 'generic.event',

        baseCls: 'text-accordion',
        containerCls: 'text-container',
        headingStyle: 'span',
        panelIDPrefix: 'seo-description-type',
        useStructuredData: false,
    };

    constructor(props) {
        super(props);

        const text = props.text || [];
        if (text.length) {
            text[0].expanded = true;
        }
        this.state = {text};

        this.itemContentRefs = [];
    }

    toggleContent = (index) => {
        return () => {
            this.toggleContentAtIndex(index);
        };
    };

    getItemContentRefSetter(index) {
        return (ref) => {
            this.itemContentRefs[index] = ref;
        };
    }

    getItemContentTransitionEndHandler(index) {
        return () => {
            this.setState((state) => {
                state.text[index].isTransitioning = false;
                return state;
            });
        };
    }

    fireItemToggledEdapEvent(item) {
        const {ACTION_LOCATION, EVENT_CATEGORY, EVENT_TYPE} = this.props;

        const event = {
            actionlocation: ACTION_LOCATION,
            eventcategory: EVENT_CATEGORY,
            eventaction: item.expanded ? 'expand' : 'collapse',
        };

        const fastpathEvent = {
            action_location: ACTION_LOCATION,
            event_category: EVENT_CATEGORY,
            event_action: item.expanded ? 'expand' : 'collapse',
        };

        if (item.panelID) {
            event.eventrefinements = item.panelID;
            fastpathEvent.event_refinements = item.panelID;
        }

        pushEdapEvent(EVENT_TYPE, event);
        pushEdapEvent('interaction.selected', fastpathEvent);
    }

    toggleContentAtIndex(index) {
        this.setState(
            (state) => {
                const item = state.text[index];

                item.expanded = !item.expanded;
                item.isTransitioning = true;

                // While expanding, this represents the height the element will transition
                // to. While collapsing, this is the height the element will
                // be initially set to before it transitions to 0.
                item.targetHeight = this.itemContentRefs[index].scrollHeight;

                return state;
            },
            () => {
                this.fireItemToggledEdapEvent(this.state.text[index]);

                this.setState((state) => {
                    const item = state.text[index];
                    const isCollapsing = item.isTransitioning && !item.expanded;
                    if (isCollapsing) {
                        // Force a reflow to ensure the initial height we previously
                        // set is applied
                        // eslint-disable-next-line
                        document.documentElement.offsetLeft;

                        // Transition the height to 0 by restoring its implicit value
                        item.targetHeight = null;
                    }
                    return state;
                });
            }
        );
    }

    structuredData(type) {
        if (this.props.useStructuredData !== true) {
            return {};
        }

        switch (type) {
            case 'faq':
                return {
                    itemScope: true,
                    itemType: 'https://schema.org/FAQPage',
                };
            case 'question':
                return {
                    itemScope: true,
                    itemProp: 'mainEntity',
                    itemType: 'https://schema.org/Question',
                };
            case 'question-name':
                return {
                    itemProp: 'name',
                };
            case 'answer':
                return {
                    itemScope: true,
                    itemProp: 'acceptedAnswer',
                    itemType: 'https://schema.org/Answer',
                };
            case 'answer-text':
                return {
                    itemProp: 'text',
                };
            default:
                return {};
        }
    }

    getHeadingTag(headingStyle, index, titleTag) {
        switch (headingStyle) {
            case 'heading':
                return index === 0 ? 'h2' : 'h3';
            case 'dynamic':
                return titleTag;
            default:
                return 'span';
        }
    }

    render() {
        const {baseCls, containerCls, headingStyle, panelIDPrefix} = this.props;

        const {text} = this.state;

        const baseClsItem = `${baseCls}-item`;

        const textComponent = text.length ? (
            <div className={'col-md-12'} {...this.structuredData('faq')}>
                {text.map((item, index) => {
                    const {
                        component = null,
                        content = null,
                        expanded,
                        isTransitioning,
                        targetHeight,
                        panelID,
                        title,
                        type,
                        titleTag,
                    } = item;
                    const panelIDLC = panelID
                        ? panelID.toLowerCase()
                        : `${baseClsItem}-${index}`;
                    const contentCls = `${baseClsItem}__content ${baseCls}-type-${type} ${panelIDPrefix}-${panelIDLC}`;
                    const contentID = `${baseClsItem}__${panelIDLC}`;
                    const HeadingTag = this.getHeadingTag(
                        headingStyle,
                        index,
                        titleTag
                    );

                    /* eslint-disable react/no-danger */
                    return (
                        <div
                            className={`${baseClsItem} ${
                                expanded ? `${baseClsItem}__expanded` : ''
                            }`}
                            id={contentID}
                            {...this.structuredData('question')}
                            key={panelIDLC}
                        >
                            <HeadingTag
                                className={`${baseClsItem}__title`}
                                {...this.structuredData('question-name')}
                                role={'button'}
                                onClick={this.toggleContent(index)}
                            >
                                <SVGIcon
                                    className={`${baseClsItem}__icon`}
                                    markup={CHEVRON_DOWN__16}
                                />
                                <span className={`${baseClsItem}__title__text`}>
                                    {title}
                                </span>
                            </HeadingTag>
                            <div
                                className={contentCls}
                                {...this.structuredData('answer')}
                                style={{
                                    height: isTransitioning
                                        ? targetHeight
                                        : null,
                                }}
                                ref={this.getItemContentRefSetter(index)}
                                onTransitionEnd={this.getItemContentTransitionEndHandler(
                                    index
                                )}
                            >
                                <div
                                    {...this.structuredData('answer-text')}
                                    {...(component
                                        ? {children: component}
                                        : {
                                              dangerouslySetInnerHTML: {
                                                  __html: content,
                                              },
                                          })}
                                />
                            </div>
                        </div>
                    );
                    /* eslint-enable react/no-danger */
                })}
            </div>
        ) : null;

        return (
            <div
                className={`container module ${containerCls} ${baseCls}`}
                ref={'container'}
            >
                <EdapViewTracker
                    eventData={{
                        action_location: this.props.ACTION_LOCATION,
                        position_fired: 'top',
                    }}
                    edapEvent={'content_module.presented'}
                />

                {textComponent}

                <EdapViewTracker
                    eventData={{actionlocation: this.props.ACTION_LOCATION}}
                >
                    <div style={{minHeight: '1px', clear: 'both'}} />
                </EdapViewTracker>
                <EdapViewTracker
                    eventData={{
                        action_location: this.props.ACTION_LOCATION,
                        position_fired: 'bottom',
                    }}
                    edapEvent={'content_module.presented'}
                />
            </div>
        );
    }
}

export default TextAccordion;
